/**
 * Server-side companion: `marshmallow.validate.Regexp(regex=r"^[a-zA-Z0-9-\._,:;\(\)\[\]\'\/\?\%\n\r +<>=&@*#]*$")`.
 *
 * The SPA mirrors the same allowlist so any value persisted into a free-text field will pass the
 * server-side schema validation and POST/PUT calls won't be rejected with a generic 400.
 *
 * Allowed characters:
 *   • letters: a-z, A-Z
 *   • digits: 0-9
 *   • punctuation: `-` `.` `_` `,` `:` `;` `(` `)` `[` `]` `'` `/` `?` `%`
 *     `+` `<` `>` `=` `&` `@` `*` `#`
 *   • whitespace: space, `\n`, `\r`
 *
 * Disallowed examples: `"` `\` `{` `}` `|` `$` `` ` `` `~` `^` `!`.
 *
 * Empty / nullish values are considered valid — required-ness is enforced separately by the caller.
 */
// Character class mirrors Python `r"^[a-zA-Z0-9-\._,:;\(\)\[\]\'\/\?\%\n\r +<>=&@*#]*$"` (`]` escaped for JS).
export const FREE_TEXT_REGEX: RegExp = /^[a-zA-Z0-9\-._,:;()+<>=&@*#[\]'/?%\n\r ]*$/

export const FREE_TEXT_INVALID_MESSAGE =
  "Only letters, numbers, spaces, and basic punctuation (-._,:;()[]'/?%+<>=&@*#) are allowed."

/** True when `value` is empty/nullish or contains only allowlisted characters. */
export function isValidFreeText(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return FREE_TEXT_REGEX.test(value)
}

/**
 * Per-character allowlist mirroring {@link FREE_TEXT_REGEX} (the same character
 * class, applied one character at a time). Used to pinpoint the *specific*
 * disallowed characters in a value so the UI can highlight them, rather than
 * only reporting that the whole field is invalid.
 */
const FREE_TEXT_CHAR_REGEX: RegExp = /[a-zA-Z0-9\-._,:;()+<>=&@*#[\]'/?%\n\r ]/

/** True when a single character is allowed by the free-text allowlist. */
export function isAllowedFreeTextChar(char: string): boolean {
  return FREE_TEXT_CHAR_REGEX.test(char)
}

/**
 * Returns the set of distinct disallowed characters present in `value` (empty
 * when the value is valid/empty). Newlines/carriage returns are allowed, so
 * they never appear here. Useful for surfacing exactly which characters must be
 * removed.
 */
export function invalidFreeTextChars(value: string | null | undefined): string[] {
  if (value === null || value === undefined || value === '') return []
  const seen = new Set<string>()
  for (const char of value) {
    if (!isAllowedFreeTextChar(char)) seen.add(char)
  }
  return Array.from(seen)
}

/** Returns the standard validation message when `value` contains disallowed characters, otherwise undefined. */
export function freeTextError(value: string | null | undefined): string | undefined {
  return isValidFreeText(value) ? undefined : FREE_TEXT_INVALID_MESSAGE
}

/**
 * MRN (medical record number) allowlist. Mirrors the server-side validation `r"^[\w_-]+$"`,
 * where `\w` is `[A-Za-z0-9_]`, so the only characters permitted are letters, digits,
 * underscore, and hyphen. At least one character is required.
 *
 * Empty / nullish values are considered valid here — required-ness is enforced separately
 * by the caller.
 */
export const MRN_REGEX: RegExp = /^[\w-]+$/

export const MRN_INVALID_MESSAGE =
  'MRN may only contain letters (a–z, A–Z), numbers (0–9), underscores (_), and hyphens (-).'

/** Server cap (`max_length=20`) for the medical record number. */
export const MRN_MAX_LENGTH = 20

export const MRN_TOO_LONG_MESSAGE = `MRN must be ${MRN_MAX_LENGTH} characters or fewer.`

/** True when `value` is empty/nullish or contains only allowlisted MRN characters. */
export function isValidMrn(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return MRN_REGEX.test(value)
}

/** Returns the MRN validation message when `value` contains disallowed characters, otherwise undefined. */
export function mrnError(value: string | null | undefined): string | undefined {
  return isValidMrn(value) ? undefined : MRN_INVALID_MESSAGE
}

/**
 * Name (first/last/middle/alias) allowlist. Mirrors the server-side field validator
 * `re.match(r"^[a-zA-Z0-9.'\- ]*$", v)`, so the only characters permitted are letters,
 * digits, period, apostrophe, hyphen, and space.
 *
 * Empty / nullish values are considered valid here — required-ness is enforced separately
 * by the caller.
 */
export const NAME_REGEX: RegExp = /^[a-zA-Z0-9.'\- ]*$/

export const NAME_INVALID_MESSAGE =
  "Names may only contain letters (a–z, A–Z), numbers (0–9), period (.), apostrophe ('), hyphen (-), and spaces."

/** Database cap for individual name fields (first/last). Stricter than the order-model `max_length=100`. */
export const NAME_MAX_LENGTH = 32

export const NAME_TOO_LONG_MESSAGE = `Name must be ${NAME_MAX_LENGTH} characters or fewer.`

/** True when `value` is empty/nullish or contains only allowlisted name characters. */
export function isValidName(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return NAME_REGEX.test(value)
}

/** Returns the name validation message when `value` contains disallowed characters, otherwise undefined. */
export function nameError(value: string | null | undefined): string | undefined {
  return isValidName(value) ? undefined : NAME_INVALID_MESSAGE
}

/**
 * Lowercase connector particles that should stay lowercased when they appear
 * *inside* a multi-part name (e.g. `van`, `de` in `van der Berg`, `Ana de la Cruz`).
 * They are still capitalized when they are the first token of the field.
 */
const NAME_CONNECTOR_PARTICLES = new Set([
  'de',
  'del',
  'de la',
  'della',
  'der',
  'di',
  'du',
  'la',
  'le',
  'van',
  'von',
  'da',
  'das',
  'dos',
  'bin',
  'binti',
  'al',
])

/** Capitalize a single alphabetic token, lowercasing the remaining characters. */
function capitalizeToken(token: string): string {
  if (token.length === 0) return token
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
}

/**
 * Proper-case a single whitespace-delimited word, preserving hyphen and apostrophe
 * sub-delimiters (`smith-jones` -> `Smith-Jones`, `o'brien` -> `O'Brien`).
 */
function properCaseWord(word: string): string {
  // Split on hyphen/apostrophe while keeping the delimiter so it can be re-joined.
  return word
    .split(/([-'])/)
    .map((part) => {
      if (part === '-' || part === "'") return part
      if (part.length === 0) return part
      return capitalizeToken(part)
    })
    .join('')
}

/**
 * Convert a person-name field to proper (title) case for display and persistence.
 *
 * Only normalizes names that are entirely one case (all UPPERCASE or all lowercase).
 * Mixed-case input (e.g. `McDonald`, `DeVries`) is assumed to be intentionally cased
 * and is returned trimmed but otherwise unchanged.
 *
 * When normalization applies, the following name-aware rules are used:
 *   • Capitalizes the first letter of each space-separated word, lowercasing the rest.
 *   • Preserves hyphens and apostrophes as sub-word delimiters (`Smith-Jones`, `O'Brien`).
 *   • Keeps lowercase connector particles (`van`, `de`, `von`, …) lowercased when they
 *     are not the first token.
 *
 * Empty / nullish values pass through unchanged so callers can normalize optional fields
 * without introducing empty strings.
 */
export function toProperCaseName(value: string | null | undefined): string {
  if (value === null || value === undefined) return value ?? ''
  const trimmed = value.trim()
  if (trimmed === '') return ''
  // Only normalize when the name is entirely one case (all UPPER or all lower).
  // Mixed-case input is assumed to be intentionally cased and is left untouched.
  const hasLower = /[a-z]/.test(trimmed)
  const hasUpper = /[A-Z]/.test(trimmed)
  if (hasLower && hasUpper) return trimmed
  const words = trimmed.split(/\s+/)
  return words
    .map((word, index) => {
      if (index > 0 && NAME_CONNECTOR_PARTICLES.has(word.toLowerCase())) {
        return word.toLowerCase()
      }
      return properCaseWord(word)
    })
    .join(' ')
}

/**
 * NPI (National Provider Identifier) allowlist. Mirrors the server-side field validator
 * `re.match(r"^\d{0,12}$", v)`, so the value may contain only digits, up to 12 of them.
 * (The pattern allows 0 digits, so an empty value passes — required-ness is enforced
 * separately by the caller.)
 */
export const NPI_REGEX: RegExp = /^\d{0,12}$/

export const NPI_INVALID_MESSAGE = 'NPI must be up to 12 digits (numbers only).'

/** True when `value` is empty/nullish or contains only up to 12 digits. */
export function isValidNpi(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return NPI_REGEX.test(value)
}

/** Returns the NPI validation message when `value` is not 0–12 digits, otherwise undefined. */
export function npiError(value: string | null | undefined): string | undefined {
  return isValidNpi(value) ? undefined : NPI_INVALID_MESSAGE
}

/**
 * NPI Registry search-query allowlist. Mirrors the provider-api `/npi/search` validator, which
 * rejects anything else with `"Query may only contain letters, numbers, spaces, hyphens, and
 * apostrophes"`. The only characters permitted are letters, digits, spaces, hyphens, and
 * apostrophes. Validating here surfaces the feedback inline (and avoids an unnecessary round-trip)
 * instead of a generic 400 from the backend.
 *
 * Empty / nullish values are considered valid — required-ness and minimum-length are enforced
 * separately by the caller.
 */
export const NPI_SEARCH_QUERY_REGEX: RegExp = /^[a-zA-Z0-9 \-']*$/

export const NPI_SEARCH_QUERY_INVALID_MESSAGE =
  'Query may only contain letters, numbers, spaces, hyphens, and apostrophes.'

/** True when `value` is empty/nullish or contains only allowlisted NPI search-query characters. */
export function isValidNpiSearchQuery(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return NPI_SEARCH_QUERY_REGEX.test(value)
}

/** Returns the NPI search-query message when `value` contains disallowed characters, otherwise undefined. */
export function npiSearchQueryError(value: string | null | undefined): string | undefined {
  return isValidNpiSearchQuery(value) ? undefined : NPI_SEARCH_QUERY_INVALID_MESSAGE
}

/**
 * Medical-title / role (RCIGM `otherProviderType`) allowlist. Mirrors the server-side field
 * validator `re.match(r"^[0-9a-zA-Z.\- ]*$", v)` (carebus-client OrderInfo.otherProviderType),
 * so the only characters permitted are digits, letters, period, hyphen, and space.
 *
 * This is stricter than the generic free-text allowlist (which permits `,`, `/`, `(`, `)`, etc.):
 * a value like `MD, PhD (Genetics)` or `RN/BSN` passes free-text validation but would be rejected
 * by the downstream lab API. Validating here surfaces the error at the field instead of a generic
 * 400 at submit.
 *
 * Empty / nullish values are considered valid — required-ness is enforced separately by the caller.
 */
export const ROLE_TITLE_REGEX: RegExp = /^[0-9a-zA-Z.\- ]*$/

export const ROLE_TITLE_INVALID_MESSAGE =
  'Medical title may only contain letters (a–z, A–Z), numbers (0–9), period (.), hyphen (-), and spaces.'

/** True when `value` is empty/nullish or contains only allowlisted medical-title characters. */
export function isValidRoleTitle(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return true
  return ROLE_TITLE_REGEX.test(value)
}

/** Returns the medical-title charset message when `value` contains disallowed characters, otherwise undefined. */
export function roleTitleError(value: string | null | undefined): string | undefined {
  return isValidRoleTitle(value) ? undefined : ROLE_TITLE_INVALID_MESSAGE
}

/** Provider-api length limit for the optional phone contact field (PATCH /auth/me & /auth/users/:id/profile). */
export const PHONE_MAX_LENGTH = 32
/**
 * Medical-title length cap. Matches the RCIGM `otherProviderType` `max_length=20` (carebus-client
 * OrderInfo) so a title that would be rejected by the downstream lab API is caught at the field.
 * This is stricter than the provider-api profile column, but the tighter of the two always wins.
 */
export const ROLE_TITLE_MAX_LENGTH = 20

/**
 * Coerce an arbitrary title string into a valid medical title.
 *
 * External sources (e.g. the NPPES NPI registry `credential` field such as
 * "MD, PhD" or "M.D., Ph.D.") commonly include commas and other characters the
 * medical-title allowlist rejects, and may exceed {@link ROLE_TITLE_MAX_LENGTH}.
 * Copying such a value straight into the field makes it invalid and blocks
 * submit. This strips disallowed characters (replacing them with a space so
 * tokens stay separated), collapses whitespace, and truncates to the length
 * cap so the result always satisfies {@link isValidRoleTitle} and
 * {@link isWithinLength}.
 *
 * @param value - Raw title value (may be empty/nullish).
 * @returns A sanitized, length-capped medical title (possibly empty).
 */
export function sanitizeRoleTitle(value: string | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value
    .replace(/[^0-9a-zA-Z.\- ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, ROLE_TITLE_MAX_LENGTH)
    .trim()
}

/** Database cap for email fields (user profile and contact emails). */
export const EMAIL_MAX_LENGTH = 64

export const PHONE_TOO_LONG_MESSAGE = `Phone must be ${PHONE_MAX_LENGTH} characters or fewer.`
export const ROLE_TITLE_TOO_LONG_MESSAGE = `Medical title must be ${ROLE_TITLE_MAX_LENGTH} characters or fewer.`
export const EMAIL_TOO_LONG_MESSAGE = `Email must be ${EMAIL_MAX_LENGTH} characters or fewer.`

/** True when the trimmed value is within `max` characters (empty values are always valid). */
export function isWithinLength(value: string | null | undefined, max: number): boolean {
  if (value === null || value === undefined) return true
  return value.trim().length <= max
}

/**
 * Maximum length of the combined clinical `description` sent to Rady (RCIGM) via CareBus.
 *
 * provider-api concatenates the clinical note, family history, differential diagnosis, and
 * prior genetic testing into a single `ClinicalInfo.description` capped at this many
 * characters. LSMC and other non-Rady labs persist the same fields without this ceiling
 * (IMDB stores them as unbounded text), so callers must apply the limit only for Rady.
 */
export const CLINICAL_DESCRIPTION_MAX_LENGTH = 10000

/** RCIGM intake (`ClinicalInfoCreate.description`) limit, applied to each comparator summary. */
export const RCIGM_COMPARATOR_CLINICAL_NOTE_MAX_LENGTH = 10000

/** Most comparators RCIGM accepts on one order. */
export const RCIGM_MAX_COMPARATORS = 3

/**
 * Order-wide budget for non-RCIGM labs, shared by the proband clinical summary, differential
 * diagnosis, prior genetic testing, family history, and every comparator summary.
 *
 * Kept below the CareBus per-event ceiling (200,000): consumers downstream of the publisher
 * misbehave on clinical text larger than this, so the order is refused while the clinician can
 * still edit it. Must stay in step with `SHARED_CLINICAL_TEXT_MAX_LENGTH` in provider-api's
 * `app/ordering/clinical_description_limit.py`, which re-checks the same total on submit.
 */
export const SHARED_CLINICAL_TEXT_MAX_LENGTH = 100000

export const RCIGM_COMPARATOR_CLINICAL_NOTE_TOO_LONG_MESSAGE = `Comparator clinical summary must be ${RCIGM_COMPARATOR_CLINICAL_NOTE_MAX_LENGTH.toLocaleString()} characters or fewer.`

export const SHARED_CLINICAL_TEXT_TOO_LONG_MESSAGE = `The order's clinical text exceeds the ${SHARED_CLINICAL_TEXT_MAX_LENGTH.toLocaleString()}-character limit shared by all summaries. Shorten this or another entry.`

export const SHARED_CLINICAL_TEXT_HELPER =
  'The counter is shared by the clinical summary, differential diagnosis, prior genetic testing, family history, and all comparator summaries.'

/** Labels/separators provider-api adds when building the combined narrative. Kept in sync so the SPA estimate matches the wire value. */
const CLINICAL_NARRATIVE_SEPARATOR = '\n\n'
const CLINICAL_FAMILY_HISTORY_LABEL = 'Family history: '
const CLINICAL_DIFFERENTIAL_LABEL = 'Differential diagnosis: '
const CLINICAL_PRIOR_GENETIC_TESTING_LABEL = 'Prior genetic testing: '

/**
 * Length of the combined clinical `description` provider-api will emit, given the
 * component fields. Mirrors `_truncate_carebus_clinical_narrative_preserving_tail`:
 *
 *   note + "\n\n" + "Family history: " + fh + "\n\n" + "Differential diagnosis: " + dd
 *     + "\n\n" + "Prior genetic testing: " + pgt
 *
 * Blocks are omitted when their source text is empty, and the leading separator is only
 * added between present blocks — matching the server so the counter reflects the true
 * value that will be validated against `CLINICAL_DESCRIPTION_MAX_LENGTH`.
 */
export function combinedClinicalDescriptionLength(
  clinicalNote: string | null | undefined,
  familyHistory: string | null | undefined,
  differentialDiagnosis: string | null | undefined,
  priorGeneticTesting?: string | null | undefined
): number {
  const note = (clinicalNote ?? '').trim()
  const fh = (familyHistory ?? '').trim()
  const dd = (differentialDiagnosis ?? '').trim()
  const pgt = (priorGeneticTesting ?? '').trim()

  const tailBlocks: string[] = []
  if (fh) tailBlocks.push(`${CLINICAL_FAMILY_HISTORY_LABEL}${fh}`)
  if (dd) tailBlocks.push(`${CLINICAL_DIFFERENTIAL_LABEL}${dd}`)
  if (pgt) tailBlocks.push(`${CLINICAL_PRIOR_GENETIC_TESTING_LABEL}${pgt}`)
  const tail = tailBlocks.join(CLINICAL_NARRATIVE_SEPARATOR)

  if (!tail) return note.length
  if (!note) return tail.length
  return note.length + CLINICAL_NARRATIVE_SEPARATOR.length + tail.length
}

/** True when the combined clinical description would be rejected for exceeding the lab ceiling. */
export function isCombinedClinicalDescriptionOverLimit(
  clinicalNote: string | null | undefined,
  familyHistory: string | null | undefined,
  differentialDiagnosis: string | null | undefined,
  priorGeneticTesting?: string | null | undefined
): boolean {
  return (
    combinedClinicalDescriptionLength(clinicalNote, familyHistory, differentialDiagnosis, priorGeneticTesting) >
    CLINICAL_DESCRIPTION_MAX_LENGTH
  )
}

/**
 * Shown when Send Updates / Submit must stay disabled because the combined
 * clinical description is over the lab ceiling. The modals already block their
 * own Done buttons; this is the order-level copy for the actions outside them.
 */
export const COMBINED_CLINICAL_DESCRIPTION_LIMIT_MESSAGE = `The combined clinical summary, differential diagnosis, prior genetic testing, and family history exceed the ${CLINICAL_DESCRIPTION_MAX_LENGTH.toLocaleString()}-character limit. Shorten your entries before sending the order.`

type CanonicalClinicalText =
  | {
      clinical?: {
        clinicalNote?: string | null
        differentialDiagnosis?: string | null
        priorGeneticTesting?: string | null
      } | null
      family?: { familyHistory?: string | null } | null
      comparators?: ReadonlyArray<{
        id?: string | null
        relationship?: string | null
        clinicalNote?: string | null
      } | null> | null
    }
  | null
  | undefined

/**
 * Characters a field may use, and how many other fields of the order already use.
 * `rcigm` budgets are per field group (Rady limits each description); `shared` is the
 * single order-wide pool for every other lab.
 */
export interface ClinicalTextBudget {
  kind: 'rcigm' | 'shared'
  limit: number
  usedElsewhere: number
}

export const RCIGM_COMBINED_CLINICAL_TEXT_BUDGET: ClinicalTextBudget = {
  kind: 'rcigm',
  limit: CLINICAL_DESCRIPTION_MAX_LENGTH,
  usedElsewhere: 0,
}

export const RCIGM_COMPARATOR_CLINICAL_NOTE_BUDGET: ClinicalTextBudget = {
  kind: 'rcigm',
  limit: RCIGM_COMPARATOR_CLINICAL_NOTE_MAX_LENGTH,
  usedElsewhere: 0,
}

function savedComparators(canonical: CanonicalClinicalText) {
  return (canonical?.comparators ?? []).filter(
    (c): c is NonNullable<typeof c> => c != null && c.relationship !== 'PROBAND'
  )
}

/** Combined proband clinical text, counted the way provider-api composes the description. */
export function probandClinicalTextLength(canonical: CanonicalClinicalText): number {
  return combinedClinicalDescriptionLength(
    canonical?.clinical?.clinicalNote,
    canonical?.family?.familyHistory,
    canonical?.clinical?.differentialDiagnosis,
    canonical?.clinical?.priorGeneticTesting
  )
}

/** Sum of trimmed comparator summaries, optionally leaving out the one being edited. */
export function comparatorClinicalNotesLength(
  canonical: CanonicalClinicalText,
  excludeComparatorId?: string | null
): number {
  return savedComparators(canonical)
    .filter((c) => !excludeComparatorId || c.id !== excludeComparatorId)
    .reduce((sum, c) => sum + (c.clinicalNote ?? '').trim().length, 0)
}

/** Everything that counts toward {@link SHARED_CLINICAL_TEXT_MAX_LENGTH}. */
export function sharedClinicalTextLength(canonical: CanonicalClinicalText): number {
  return probandClinicalTextLength(canonical) + comparatorClinicalNotesLength(canonical)
}

/** Budget for the proband clinical summary and family history modals. */
export function probandClinicalTextBudget(strategy: string, canonical: CanonicalClinicalText): ClinicalTextBudget {
  if (strategy === 'rcigm') return RCIGM_COMBINED_CLINICAL_TEXT_BUDGET
  return {
    kind: 'shared',
    limit: SHARED_CLINICAL_TEXT_MAX_LENGTH,
    usedElsewhere: comparatorClinicalNotesLength(canonical),
  }
}

/** Budget for one comparator summary; pass `null` for a comparator that is not saved yet. */
export function comparatorClinicalNoteBudget(
  strategy: string,
  canonical: CanonicalClinicalText,
  comparatorId: string | null | undefined
): ClinicalTextBudget {
  if (strategy === 'rcigm') return RCIGM_COMPARATOR_CLINICAL_NOTE_BUDGET
  return {
    kind: 'shared',
    limit: SHARED_CLINICAL_TEXT_MAX_LENGTH,
    usedElsewhere: probandClinicalTextLength(canonical) + comparatorClinicalNotesLength(canonical, comparatorId),
  }
}

export const RCIGM_COMPARATOR_NOTE_LIMIT_MESSAGE = `A comparator clinical summary exceeds the ${RCIGM_COMPARATOR_CLINICAL_NOTE_MAX_LENGTH.toLocaleString()}-character limit. Shorten it before sending the order.`

export const RCIGM_COMPARATOR_COUNT_MESSAGE = `This order has more than ${RCIGM_MAX_COMPARATORS} comparators. Remove one before sending the order.`

export const SHARED_CLINICAL_TEXT_LIMIT_MESSAGE = `The order's clinical text exceeds the ${SHARED_CLINICAL_TEXT_MAX_LENGTH.toLocaleString()}-character limit shared by the clinical summary, differential diagnosis, prior genetic testing, family history, and comparator summaries. Shorten your entries before sending the order.`

/**
 * Order-level message when the clinical text cannot be sent to the lab, or `null` when it can.
 * Gates Continue / Submit / Send Updates, which sit outside the modals that block their own Done.
 */
export function clinicalTextLimitError(strategy: string, canonical: CanonicalClinicalText): string | null {
  if (strategy === 'rcigm') {
    if (probandClinicalTextLength(canonical) > CLINICAL_DESCRIPTION_MAX_LENGTH) {
      return COMBINED_CLINICAL_DESCRIPTION_LIMIT_MESSAGE
    }
    const comparators = savedComparators(canonical)
    if (comparators.some((c) => (c.clinicalNote ?? '').trim().length > RCIGM_COMPARATOR_CLINICAL_NOTE_MAX_LENGTH)) {
      return RCIGM_COMPARATOR_NOTE_LIMIT_MESSAGE
    }
    if (comparators.length > RCIGM_MAX_COMPARATORS) return RCIGM_COMPARATOR_COUNT_MESSAGE
    return null
  }
  return sharedClinicalTextLength(canonical) > SHARED_CLINICAL_TEXT_MAX_LENGTH
    ? SHARED_CLINICAL_TEXT_LIMIT_MESSAGE
    : null
}

/**
 * Permissive phone-structure check for the optional contact field.
 *
 * Accepts common formats — e.g. `5551234567`, `555-123-4567`, `(555) 123-4567`,
 * `+1 555 123 4567`, optionally with an extension (`x123`, `ext. 123`). Requires
 * 7–15 digits (ITU E.164 upper bound) so obviously malformed values are rejected
 * while still allowing international numbers. Empty/nullish values are valid;
 * required-ness is enforced separately by the caller.
 */
export const PHONE_INVALID_MESSAGE = 'Enter a valid phone number.'

const PHONE_STRUCTURE_REGEX = /^\+?[0-9().\-\s]{7,}(?:\s*(?:x|ext\.?|extension)\s*\d{1,6})?$/i

export function isValidPhone(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return true
  const trimmed = value.trim()
  if (trimmed === '') return true
  if (!PHONE_STRUCTURE_REGEX.test(trimmed)) return false
  const digits = trimmed.replace(/\D/g, '')
  return digits.length >= 7 && digits.length <= 15
}
